import React from "react"

import { Tooltip } from "@coval/ui"
import { Radio, RadioGroup } from "@heroui/react"
import { MessageSquare, Wand2 } from "lucide-react"

import type { ImprovementFocus } from "../optimization/optimization"

interface ImprovementFocusSelectorProps {
  value: ImprovementFocus
  onChange: (focus: ImprovementFocus) => void
  hasFeedback: boolean
  hasHighMachineDisagreement: boolean
}

interface FocusOption {
  value: ImprovementFocus
  label: string
  description: string
  icon: React.ReactNode
  recommended?: boolean
  disabled?: boolean
  disabledReason?: string
}

export const ImprovementFocusSelector = ({
  value,
  onChange,
  hasFeedback,
  hasHighMachineDisagreement,
}: ImprovementFocusSelectorProps) => {
  const options: FocusOption[] = [
    {
      value: "general",
      label: "General suggestions",
      description: "Suggest clarity, structure, and best practice improvements",
      icon: <Wand2 className="w-4 h-4" aria-hidden="true" />,
      recommended: !hasFeedback,
    },
    {
      value: "feedback",
      label: "Address feedback issues",
      description: "Focus on reviewer notes and human-AI label differences",
      icon: <MessageSquare className="w-4 h-4" aria-hidden="true" />,
      recommended: hasFeedback && hasHighMachineDisagreement,
      disabled: !hasFeedback,
      disabledReason:
        "No annotations available. Add annotations to enable this option.",
    },
  ]

  const renderOption = (option: FocusOption) => {
    const radioElement = (
      <Radio
        key={option.value}
        value={option.value}
        isDisabled={option.disabled}
        aria-label={option.label}
        classNames={{
          base: `inline-flex m-0 bg-surface-0 items-start justify-between flex-row-reverse max-w-full rounded-lg gap-4 p-3 border-2 border-transparent data-[selected=true]:border-coval-border-active ${
            option.disabled
              ? "opacity-50 cursor-default w-full"
              : "hover:bg-surface-1 cursor-pointer"
          }`,
          labelWrapper: "flex-1",
        }}
      >
        <div className="flex items-start gap-3 w-full">
          <div className="p-1.5 bg-surface-1 rounded-md">{option.icon}</div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm">{option.label}</span>
              {option.recommended && !option.disabled && (
                <span className="text-xs bg-coval-info-surface text-coval-info-on-surface px-1.5 py-0.5 rounded">
                  Recommended
                </span>
              )}
            </div>
            <p className="text-xs text-coval-text-muted mt-0.5">
              {option.description}
            </p>
          </div>
        </div>
      </Radio>
    )

    if (option.disabled && option.disabledReason) {
      return (
        <Tooltip key={option.value} content={option.disabledReason} side="top">
          {radioElement}
        </Tooltip>
      )
    }

    return radioElement
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-coval-text-muted font-medium uppercase">
        Suggestion Focus
      </p>
      <RadioGroup
        value={value}
        onValueChange={(val) => onChange(val as ImprovementFocus)}
        aria-label="Select suggestion focus"
        classNames={{
          wrapper: "gap-2",
        }}
      >
        {options.map(renderOption)}
      </RadioGroup>
    </div>
  )
}
